import React from 'react';
import {graphql} from 'react-relay';
// NAMED export. ForecastQueryRenderer.js:16 is `export const ForecastQueryRenderer`
// and has no default; a default import resolves to undefined and React throws
// "Element type is invalid" the first time the drawer opens.
import {ForecastQueryRenderer} from '../../../ForecastQueryRenderer';
import {ManageTaskSetsDrawer} from '../../../forecast-app/project-tab/projects/scoping-page/task-set/ManageTaskSetsDrawer';

// `project(id: $projectId)` with a String argument -- the convention this page
// already uses at canvas_scheduling.js:496.
const query = graphql`
	query TimelineManageTaskSetsDrawer_Query($projectId: String) {
		viewer {
			actualPersonId
			component(name: "timeline_manage_task_sets")
			project(id: $projectId) {
				...ManageTaskSetsDrawer_project
			}
		}
	}
`;

export interface TimelineManageTaskSetsDrawerProps {
	/** The project's custom or company id. Null on a project group timeline. */
	projectId: string | null;
	isOpen: boolean;
	initialSetId?: string | null;
	/** Passed straight through; see ManageTaskSetsDrawer's openNonce. */
	openNonce?: number;
	onClose: () => void;
}

/**
 * The manage drawer on the project timeline.
 *
 * The set list is fetched when the surface opens rather than on the page query:
 * the timeline's query is already the heaviest in the app, and instanceCount and
 * exceptionCount across every set on a project are payload nobody reads until
 * this drawer is on screen. The handful of scalars the CHIP needs are a different
 * matter and do ride on the page query -- see canvasScheduling_viewer.
 *
 * Renders nothing without a project: the group timeline route resolves none.
 *
 * PIKA-773: this also carried an `isPsProject` refusal for AC4's PS half, fed from
 * `!!viewer.psProject`. That is not a PS-only signal (ViewerType.js:797 resolves it
 * from the project service for ordinary projects too), so the drawer refused to
 * render on every project timeline. Removed rather than re-guessed -- see the note
 * in projects_scheduling_header.js.
 */
export const TimelineManageTaskSetsDrawer = ({
	projectId,
	isOpen,
	initialSetId = null,
	openNonce = 0,
	onClose,
}: TimelineManageTaskSetsDrawerProps) => {
	const [hasOpened, setHasOpened] = React.useState(false);

	React.useEffect(() => {
		if (isOpen) {
			setHasOpened(true);
		}
	}, [isOpen]);

	// No project, no set list -- the group timeline route resolves none.
	if (!projectId || !hasOpened) {
		// `hasOpened` keeps the drawer mounted after the first open so TaskSetDrawer
		// can run its slide-out transition -- returning null on !isOpen would make
		// the drawer vanish instead of closing. The query still does not run until
		// the first open, which is the point of fetching here rather than on the
		// page query.
		return null;
	}

	return (
		<ForecastQueryRenderer
			key="query-render-timeline-manage-task-sets"
			query={query}
			variables={{projectId}}
			customLoader={() => null}
			// ForecastQueryRenderer.js:169 calls props.render(relayProps, retry) with
			// the QUERY ROOT, so the project hangs off relayProps.viewer -- reading
			// relayProps.project would be undefined and the list would render empty
			// with no error.
			render={(relayProps: {viewer?: {project?: unknown}}) => (
				<ManageTaskSetsDrawer
					isOpen={isOpen}
					project={relayProps?.viewer?.project}
					initialSetId={initialSetId}
					openNonce={openNonce}
					onClose={onClose}
				/>
			)}
		/>
	);
};
